import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Center, Separator, HStack, Icon, Spacer, Spinner, Text, VStack } from '@chakra-ui/react';
import { useContentTrackContext } from '@src/components/sco/Education/ContentTrackContext';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { RichMarkdownInput } from '@src/components/sco/RichMarkdownInput';
import { ViewableTracker } from '@src/components/sco/ViewableTracker';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { ContentTrackPrompt, ContentTrackStep, ContentTrackTask } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import { useDebounce } from '@uidotdev/usehooks';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { BsInfoCircle } from 'react-icons/bs';
type PromptProps = {
  contentTrackStep: ContentTrackStep;
  task: ContentTrackTask;
  prompt: ContentTrackPrompt;
};
export const Prompt = props => {
  const $ = _c(73);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const trackContext = useContentTrackContext();
  const editorRef = useRef(null);
  const [markdownValue, setMarkdownValue] = useState("");
  const [contentTrackResponseId, setContentTrackResponseId] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = gql`
      query ($contentTrackEnrollmentId: String, $contentTrackTaskId: String) {
        scoContentTrackResponse(
          contentTrackEnrollmentId: $contentTrackEnrollmentId
          contentTrackTaskId: $contentTrackTaskId
        ) {
          id
          contentTrackId
          contentTrackResponseId
          contentTrackSlug
          contentTrackStepId
          contentTrackStepSlug
          contentTrackTaskId
          contentTrackTaskSlug
          value
          valueFormat
          valueContext
          updatedAt
        }
      }
    `;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = trackContext.contentTrackEnrollment?.contentTrackEnrollmentId;
  let t2;
  if ($[1] !== props.task.id || $[2] !== t1) {
    t2 = {
      variables: {
        contentTrackEnrollmentId: t1,
        contentTrackTaskId: props.task.id
      }
    };
    $[1] = props.task.id;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const query = useQuery(t0, t2);
  let t3;
  if ($[4] !== markdownValue || $[5] !== query.data) {
    t3 = () => {
      setContentTrackResponseId(query.data?.scoContentTrackResponse?.contentTrackResponseId);
      if (!markdownValue && query.data?.scoContentTrackResponse?.value) {
        setMarkdownValue(query.data.scoContentTrackResponse.value);
      }
    };
    $[4] = markdownValue;
    $[5] = query.data;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = query.data?.scoContentTrackResponse?.contentTrackResponseId;
  const t5 = query.data?.scoContentTrackResponse?.value;
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = [t4, t5];
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useEffect(t3, t6);
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = gql`
    mutation ($input: UpsertContentTrackResponseInput!) {
      upsertContentTrackResponse(input: $input) {
        contentTrackResponse {
          id
          contentTrackId
          contentTrackResponseId
          contentTrackSlug
          contentTrackStepId
          contentTrackStepSlug
          contentTrackTaskId
          contentTrackTaskSlug
          value
          valueFormat
          valueContext
          updatedAt
        }
      }
    }
  `;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const [upsertResponse, upsertResponseMutation] = useMutation(t7);
  const response = query.data?.scoContentTrackResponse;
  let t8;
  if ($[11] !== contentTrackResponseId || $[12] !== markdownValue || $[13] !== props.contentTrackStep.id || $[14] !== props.contentTrackStep.parentContentTrackSection.parentContentTrack.id || $[15] !== props.contentTrackStep.parentContentTrackSection.parentContentTrack.slug || $[16] !== props.contentTrackStep.slug || $[17] !== props.task.id || $[18] !== props.task.slug || $[19] !== query || $[20] !== trackContext.contentTrackEnrollment?.contentTrackEnrollmentId || $[21] !== upsertResponse) {
    t8 = async t9 => {
      const e = t9 === undefined ? null : t9;
      if (e) {
        e.preventDefault();
      }
      await upsertResponse({
        variables: {
          input: {
            contentTrackEnrollmentId: trackContext.contentTrackEnrollment?.contentTrackEnrollmentId,
            contentTrackResponseId,
            contentTrackId: props.contentTrackStep.parentContentTrackSection.parentContentTrack.id,
            contentTrackSlug: props.contentTrackStep.parentContentTrackSection.parentContentTrack.slug,
            contentTrackStepId: props.contentTrackStep.id,
            contentTrackStepSlug: props.contentTrackStep.slug,
            contentTrackTaskId: props.task.id,
            contentTrackTaskSlug: props.task.slug,
            valueFormat: "markdown",
            value: markdownValue,
            completedAt: markdownValue ? new Date() : null
          }
        }
      });
      if (!query.data?.scoContentTrackResponse) {
        await query.refetch();
      }
      editorRef.current && editorRef.current.focus();
    };
    $[11] = contentTrackResponseId;
    $[12] = markdownValue;
    $[13] = props.contentTrackStep.id;
    $[14] = props.contentTrackStep.parentContentTrackSection.parentContentTrack.id;
    $[15] = props.contentTrackStep.parentContentTrackSection.parentContentTrack.slug;
    $[16] = props.contentTrackStep.slug;
    $[17] = props.task.id;
    $[18] = props.task.slug;
    $[19] = query;
    $[20] = trackContext.contentTrackEnrollment?.contentTrackEnrollmentId;
    $[21] = upsertResponse;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleSubmit = t8;
  const debouncedValue = useDebounce(markdownValue, 1000);
  let t9;
  if ($[23] !== debouncedValue || $[24] !== handleSubmit || $[25] !== isMounted) {
    t9 = () => {
      if (isMounted && debouncedValue) {
        handleSubmit();
      }
    };
    $[23] = debouncedValue;
    $[24] = handleSubmit;
    $[25] = isMounted;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== debouncedValue || $[28] !== isMounted) {
    t10 = [debouncedValue, isMounted];
    $[27] = debouncedValue;
    $[28] = isMounted;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setIsMounted(true);
    };
    t12 = [];
    $[30] = t11;
    $[31] = t12;
  } else {
    t11 = $[30];
    t12 = $[31];
  }
  useEffect(t11, t12);
  const t13 = query.data?.scoContentTrackResponse?.value ? 100 : 0;
  const t14 = trackContext.contentTrackEnrollment?.contentTrackEnrollmentId;
  const t15 = props.task.id;
  const t16 = props.task.__typename;
  const t17 = props.prompt.inputLabel || props.prompt.title;
  let t18;
  if ($[32] !== t17) {
    t18 = <Text textStyle="labelMd">{t17}</Text>;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== query.data?.scoContentTrackResponse) {
    t19 = !query.data?.scoContentTrackResponse ? <Center><Spinner /></Center> : <RichMarkdownInput key={query.data?.scoContentTrackResponse?.id || "prompt"} initialMarkdownValue={query.data?.scoContentTrackResponse?.value || ""} onChange={values => {
      setMarkdownValue(values.markdown);
    }} />;
    $[34] = query.data?.scoContentTrackResponse;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== getBreakpointValue) {
    t20 = getBreakpointValue(["space-between", "space-between", "space-between", "flex-end"]);
    $[36] = getBreakpointValue;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== getBreakpointValue) {
    t21 = getBreakpointValue(["none", "none", "none", "initial"]);
    $[38] = getBreakpointValue;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t21) {
    t22 = <Separator display={t21} flex={1} variant="dashed" />;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== response) {
    t23 = response?.updatedAt && <Text textStyle="labelSm">{"Last edit: "}{moment(response.updatedAt).fromNow()}</Text>;
    $[42] = response;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  const t24 = upsertResponseMutation.loading ? "Saving..." : "Save";
  let t25;
  if ($[44] !== t24 || $[45] !== upsertResponseMutation.loading) {
    t25 = <Button asChild={true} bgColor="backgroundTertiary" size="xs"><button disabled={upsertResponseMutation.loading} type="submit">{t24}</button></Button>;
    $[44] = t24;
    $[45] = upsertResponseMutation.loading;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t20 || $[48] !== t22 || $[49] !== t23 || $[50] !== t25) {
    t26 = <HStack color="colorPalette.fg/50" fontSize="0.75em" gap={4} marginTop="0.25em" paddingY={2} width="full" justifyContent={t20}>{t22}{t23}{t25}</HStack>;
    $[47] = t20;
    $[48] = t22;
    $[49] = t23;
    $[50] = t25;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== t19 || $[53] !== t26) {
    t27 = <Box width="full">{t19}{t26}</Box>;
    $[52] = t19;
    $[53] = t26;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== t18 || $[56] !== t27) {
    t28 = <VStack align="flex-start" gap={4} width="full">{t18}{t27}</VStack>;
    $[55] = t18;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== getBreakpointValue || $[59] !== props.prompt.exampleResponse) {
    t29 = props.prompt.exampleResponse && <Box bgColor="containerSecondary" borderRadius="md" padding={getBreakpointValue([8])} width="full"><HStack><Icon asChild={true} color="primary"><BsInfoCircle /></Icon><Text textStyle="badgeXs">Example Response</Text></HStack><Spacer height={4} /><MarkdownRenderer>{props.prompt.exampleResponse}</MarkdownRenderer></Box>;
    $[58] = getBreakpointValue;
    $[59] = props.prompt.exampleResponse;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== t28 || $[62] !== t29) {
    t30 = <VStack width="full" align="flex-start" gap={4}>{t28}{t29}</VStack>;
    $[61] = t28;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== handleSubmit || $[65] !== t30) {
    t31 = <Box borderRadius="lg" width="full"><form onSubmit={handleSubmit}>{t30}</form></Box>;
    $[64] = handleSubmit;
    $[65] = t30;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== props.task.__typename || $[68] !== props.task.id || $[69] !== t13 || $[70] !== t14 || $[71] !== t31) {
    t32 = <ViewableTracker completionAsPercent={t13} parentId={t14} parentType="ContentTrackEnrollment" viewableId={t15} viewableType={t16}>{t31}</ViewableTracker>;
    $[67] = props.task.__typename;
    $[68] = props.task.id;
    $[69] = t13;
    $[70] = t14;
    $[71] = t31;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  return t32;
};